import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';

import { FormattedMessage, injectIntl } from 'react-intl';
import momentPropTypes from 'react-moment-proptypes';
import { FaUndo } from 'react-icons/fa';
import classNames from 'classnames';
import isEqual from 'lodash-es/isEqual';

import intlShape from '@/shared/utils/intlShape';
import RelationSearch from '@/shared/components/input/RelationSearch';
import RelationTypeButtonGroup from '@/shared/components/RelationTypeButtonGroup';
import InvoiceScheduleTypeMultiSelect from '@/shared/components/input/multiselect/InvoiceScheduleTypeMultiSelect';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import DataCacheService from '@/shared/services/DataCacheService';

import FormattedCurrency from '@/shared/components/FormattedCurrency';
import { hasMultipleCompanies } from '@/shared/components/security/HasMultiCompany';
import CompanyMultiSelect from '@/shared/components/input/multiselect/CompanyMultiSelect';
import AssignmentTemplateSelector from '@/shared/components/input/AssignmentTemplateSelector';
import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import UserSearch from '@/shared/components/input/UserSearch';
import ResourceAutoComplete from '@/shared/components/registration/ResourceAutoComplete';
import { retrieveAllResources } from '@/shared/components/registration/resourceApi';
import userStorage from '@/shared/services/userStorage';
import SavedFiltersButton from '@/shared/components/SavedFiltersButton';
import LegalFormMultiSelect from '@/shared/components/input/multiselect/LegalFormMultiSelect';
import CrmTypeButtonGroup from '@/shared/components/CrmTypeButtonGroup';
import DynamicFilter, {
  getDynamicFiltersFromDefaultFilters,
  handleDynamicFiltersChanged,
  initDynamicFilters,
  resetDynamicFilters,
} from '../../../shared/components/DynamicFilter';
import FilterByLockedRegistrations from './FilterByLockedRegistrations';

import config from '../../../config';
import api from '../../../shared/api';
import messages from '../invoicingMessages';

export const defaultFilters = {
  dateFrom: null,
  dateUntil: null,
  relationId: null,
  relationName: null,
  invoiceStreamId: null,
  relationTypes: [],
  crmTypes: [],
  legalForms: [],
  ownCompanies: [],
  legalEntities: [],
  assignmentTemplateId: null,
  vatPeriodicities: [],
  withLabels: [],
  withoutLabels: [],
  withEmployeeLabels: [],
  withoutEmployeeLabels: [],
  invoiceScheduleTypes: [],
  assistant: null,
  responsible: null,
  associate: null,
  assistantBackup: null,
  billingUser: null,
  seniorUser: null,
  resourceId: null,
  referenceYear: null,
  onlyActiveRelations: false,
  filterBehaviours: {
    withLabels: 1,
    withEmployeeLabels: 1,
  },
  relationGroups: [],
  relationHandlingLevels: [],
  isLocked: null,
};

const dynamicFiltersStorageKey = 'ToInvoiceDynamicFilters';
const allDynamicFilters = [
  ...getDynamicFiltersFromDefaultFilters(
    defaultFilters,
    'relationTypes',
    'crmTypes',
    'legalForms',
    'invoiceScheduleTypes',
    'ownCompanies',
    'assignmentTemplateId',
  ),
];

class ToInvoiceFilters extends Component {
  state = {
    labels: [],
    employeeLabels: [],
    dynamicFilters: [],
  };

  static propTypes = {
    onFilterChange: PropTypes.func.isRequired,
    filters: PropTypes.shape({
      relationId: PropTypes.string,
      dateFrom: momentPropTypes.momentObj,
      dateUntil: momentPropTypes.momentObj,
      period: PropTypes.oneOf(['all', 'month', 'quarter', 'year', 'year-1', 'year-2']),
      relationTypes: PropTypes.arrayOf(PropTypes.number),
      crmTypes: PropTypes.arrayOf(PropTypes.number),
      legalForms: PropTypes.arrayOf(PropTypes.number),
      assignmentTemplateId: PropTypes.string,
      ownCompanies: PropTypes.arrayOf(PropTypes.string),
      withLabels: PropTypes.arrayOf(PropTypes.string),
      withoutLabels: PropTypes.arrayOf(PropTypes.string),
      withEmployeeLabels: PropTypes.arrayOf(PropTypes.string),
      withoutEmployeeLabels: PropTypes.arrayOf(PropTypes.string),
      invoiceScheduleTypes: PropTypes.arrayOf(PropTypes.string),
      relationGroups: PropTypes.arrayOf(PropTypes.string),
      vatPeriodicities: PropTypes.arrayOf(PropTypes.string),
      responsible: PropTypes.string,
      assistant: PropTypes.string,
      associate: PropTypes.string,
      assistantBackup: PropTypes.string,
      billingUser: PropTypes.string,
      seniorUser: PropTypes.string,
      userId: PropTypes.string,
      resourceId: PropTypes.string,
      referenceYear: PropTypes.number,
      onlyActiveRelations: PropTypes.bool,
      relationHandlingLevels: PropTypes.arrayOf(PropTypes.number),
    }),
    countSelected: PropTypes.string,
    intl: intlShape.isRequired,
  };

  static defaultProps = {
    filters: defaultFilters,
    countSelected: '',
  };
  componentWillUnmount() {
    if (this.labelCancelToken && !this.labelCancelToken.token.reason) {
      this.labelCancelToken.cancel('Operation canceled due to leaving page.');
    }
    if (this.employeeLabelCancelToken && !this.employeeLabelCancelToken.token.reason) {
      this.employeeLabelCancelToken.cancel('Operation canceled due to leaving page.');
    }
  }
  componentDidMount() {
    const { intl, filters } = this.props;
    this.assignmentsEnabled = DataCacheService.assignmentsEnabled();
    this.allowRegistrationLocking = DataCacheService.getDefaultCompany()?.allowRegistrationLocking;

    this.labelCancelToken = Axios.CancelToken.source();
    this.employeeLabelCancelToken = Axios.CancelToken.source();
    const labelEndpoint = `${config.appUrls.api}/api/resource/tags`;
    const employeeLabelEndpoint = `${config.appUrls.api}/api/employee/tags`;

    Promise.all([
      api.base.get(labelEndpoint, {
        cancelToken: this.labelCancelToken.token,
      }),
      api.base.get(employeeLabelEndpoint, {
        cancelToken: this.employeeLabelCancelToken.token,
      }),
      retrieveAllResources(intl),
    ])
      .then(([{ data: labels }, { data: employeeLabels }, resources]) => {
        this.setState({
          labels: labels
            .map(x => ({ id: x.id, label: x.name, sort: x.name.toLowerCase() }))
            .sort(dynamicSort('sort')),
          employeeLabels: employeeLabels
            .map(x => ({ id: x.id, label: x.name, sort: x.name.toLowerCase() }))
            .sort(dynamicSort('sort')),
          resources,
          dynamicFilters: initDynamicFilters(
            intl,
            allDynamicFilters,
            userStorage.getItem(dynamicFiltersStorageKey) || [],
            filters,
            defaultFilters,
          ),
        });
      })
      .catch(error => {
        // Only handle non-cancellation errors
        if (!Axios.isCancel(error)) {
          console.error('Error loading filter data:', error);
        }
      });
  }

  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };

    if (JSON.stringify(filters) !== JSON.stringify(newFilters)) onFilterChange(newFilters);
  };
  dynamicFilterChanged = (item, selected) => {
    this.setState(({ dynamicFilters }) => {
      const { filters } = this.props;
      const newFilters = handleDynamicFiltersChanged(
        dynamicFilters,
        item,
        selected,
        filters,
        defaultFilters,
      );
      userStorage.setItem(
        dynamicFiltersStorageKey,
        newFilters.filter(x => x.selected).map(x => x.id),
      );
      if (
        !selected &&
        item.id !== 'selectAll' &&
        !isEqual(filters[item.id], defaultFilters[item.id])
      ) {
        // Reset filter when it's removed
        this.filterChanged({ [item.id]: defaultFilters[item.id] });
      }
      return { dynamicFilters: newFilters };
    });
  };

  savedFilterChanged = filters => {
    const { onFilterChange } = this.props;
    const newFilters = { ...defaultFilters, ...filters };
    if (typeof newFilters.dateFrom === 'string') newFilters.dateFrom = moment.utc(filters.dateFrom);
    if (typeof newFilters.dateUntil === 'string')
      newFilters.dateUntil = moment.utc(filters.dateUntil);

    onFilterChange(newFilters);

    this.setState(({ dynamicFilters }) => ({
      dynamicFilters: resetDynamicFilters(dynamicFilters, newFilters, defaultFilters),
    }));
  };

  renderDynamicFilter = ({ type, filters, intl }) => {
    //
    console.warn(`Missing filter: ${type}`);
    return null;
  };

  relationChanged = item => {
    if (!item || !item.id) {
      this.filterChanged({ relationId: null, relationName: null, invoiceStreamId: null });
    } else {
      this.filterChanged({
        relationId: item.key,
        relationName: item.label,
      });
    }
  };
  dateChanged = (startDate, endDate) => {
    this.filterChanged({ dateFrom: startDate, dateUntil: endDate });
  };

  render() {
    const { labels, employeeLabels, resources, dynamicFilters } = this.state;
    const { filters, intl, totals, countSelected } = this.props;
    const initRelation = filters.relationId
      ? {
          key: filters.relationId,
          label: filters.relationName,
          invoiceStreamId: filters.invoiceStreamId,
        }
      : null;

    const showCompanies = hasMultipleCompanies();

    const visibleFilters = dynamicFilters.filter(x => x.selected).map(x => x.id);

    return (
      <div>
        <div className="row">
          <div className="col-md-4 col-md-push-8">
            <div className="row">
              <div className="col-sm-4 widget-block">
                <div className="info-block-separator" />
                <span className="center">
                  <h2>
                    <FormattedMessage {...messages.TotalAmount} />
                  </h2>
                  <h1 className="bold">
                    <FormattedCurrency value={(totals && totals.totalAmount) || 0} />
                  </h1>
                </span>
              </div>
              <div className="col-sm-4 widget-block">
                <div className="info-block-separator" />
                <span className="center">
                  <h2>
                    <FormattedMessage {...messages.TotalToInvoiceAmount} />
                  </h2>
                  <h1 className="bold">
                    <FormattedCurrency value={(totals && totals.toInvoiceAmount) || 0} />
                  </h1>
                </span>
              </div>
              <div className="col-sm-4 widget-block">
                <span className="center">
                  <h2>
                    <FormattedMessage {...messages.TotalHours} />
                  </h2>
                  <h1 className="bold">{((totals && totals.totalHours) || 0).toTime()}</h1>
                </span>
              </div>
            </div>
          </div>
          <div className="col-md-8 col-md-pull-4">
            <div className="row">
              <div className="col-lg-6 col-md-6">
                <div className="form-group">
                  <RelationSearch
                    onChange={this.relationChanged}
                    initialSelectedItem={initRelation}
                  />
                </div>
              </div>
              <div className="col-lg-6 col-md-6">
                <div className="form-group">
                  <RelationTypeButtonGroup
                    selectedTypes={filters.relationTypes}
                    onChange={value => {
                      this.filterChanged({ relationTypes: value });
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-lg-6 col-md-6">
                <div className="form-group">
                  <LegalFormMultiSelect
                    selection={filters.legalForms}
                    onChange={selection => this.filterChanged({ legalForms: selection })}
                    showWithoutOption
                  />
                </div>
              </div>
              <div className="col-lg-6 col-md-6">
                <div className="form-group">
                  <CrmTypeButtonGroup
                    selectedTypes={filters.crmTypes}
                    onChange={value => {
                      this.filterChanged({ crmTypes: value });
                    }}
                    hiddenTypes={[0]}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-4">
            <DelayedDateRangePicker
              startDate={filters.dateFrom}
              startDateId="FilterDateFrom"
              endDate={filters.dateUntil}
              endDateId="FilterDateUntil"
              onDatesChange={({ startDate, endDate }) => this.dateChanged(startDate, endDate)}
              block
            />
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <UserSearch
                onChange={user => this.filterChanged({ userId: user ? user.id : null })}
                value={filters.userId}
                placeholder={intl.formatMessage(messages.RegisteredBy)}
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <ResourceAutoComplete
                items={resources}
                placeholder={intl.formatMessage(messages.PriceListItem)}
                onChange={resource =>
                  this.filterChanged({ resourceId: resource ? resource.id : null })
                }
                emptyLabel={intl.formatMessage(messages.NoPriceListItemsFound)}
                value={filters.resourceId ? { id: filters.resourceId } : undefined}
                intl={intl}
                disabled={false}
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <InvoiceScheduleTypeMultiSelect
                selection={filters.invoiceScheduleTypes}
                onChange={items => this.filterChanged({ invoiceScheduleTypes: items })}
                hideInvoicingVia
              />
            </div>
          </div>

          {showCompanies && (
            <div className="col-md-4">
              <div className="form-group">
                <CompanyMultiSelect
                  selection={filters.ownCompanies}
                  onChange={companies => this.filterChanged({ ownCompanies: companies })}
                />
              </div>
            </div>
          )}
          {this.allowRegistrationLocking && (
            <FilterByLockedRegistrations
              value={filters.isLocked}
              onChange={isLocked => this.filterChanged({ isLocked: isLocked })}
            />
          )}
          {this.assignmentsEnabled && (
            <div className="col-md-4">
              <div className="form-group">
                <AssignmentTemplateSelector
                  value={filters.assignmentTemplateId}
                  onChange={id => this.filterChanged({ assignmentTemplateId: id || null })}
                  showDefaultAssignment
                />
              </div>
            </div>
          )}
          {visibleFilters && visibleFilters.length > 0
            ? visibleFilters.map(id => (
                <DynamicFilter
                  key={id}
                  type={id}
                  intl={intl}
                  labels={labels}
                  employeeLabels={employeeLabels}
                  filters={filters}
                  filterChanged={this.filterChanged}
                  relationUserSearchParams={{ withUnassigned: true }}
                  renderCustomFilter={this.renderDynamicFilter}
                  labelsIdProp="id"
                />
              ))
            : null}
        </div>
        <div className="row">
          <div className="col-sm-6">
            <SavedFiltersButton
              screen="ToInvoice"
              currentFilters={filters}
              defaultFilters={defaultFilters}
              onFiltersChanged={this.savedFilterChanged}
              dynamicFilters={dynamicFilters}
              onDynamicFiltersChanged={this.dynamicFilterChanged}
            />
          </div>
          {countSelected && (
            <div className="col-md-6 margin-10B margin5T pull-bottom text-right">
              <div>{countSelected}</div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default injectIntl(ToInvoiceFilters);
